import { ColumnProfile } from '../types';
import { buildFrequencyMap, formatNum, isNullish } from './dataUtils';


export function computeMissingValues( columns: string[], rows: Record<string, unknown>[] ) {
  return columns.map( col => {
    const nullCount = rows.filter( r => isNullish( r[col] ) ).length;
    const nullPercent = rows.length > 0 ? ( nullCount / rows.length ) * 100 : 0;
    return { column: col, nullCount, nullPercent };
  } ).sort( ( a, b ) => b.nullPercent - a.nullPercent );
}

export function computeCorrelation( colA: number[], colB: number[] ): number {
  const n = colA.length;
  if ( n === 0 ) return 0;
  const meanA = colA.reduce( ( s, v ) => s + v, 0 ) / n;
  const meanB = colB.reduce( ( s, v ) => s + v, 0 ) / n;
  let num = 0;
  let denA = 0;
  let denB = 0;
  for ( let i = 0; i < n; i++ ) {
    const da = colA[i] - meanA;
    const db = colB[i] - meanB;
    num += da * db;
    denA += da * da;
    denB += db * db;
  }
  const den = Math.sqrt( denA * denB );
  return den === 0 ? 0 : num / den;
}

export function getNumericValues( col: string, rows: Record<string, unknown>[] ): number[] {
  return rows
    .map( r => r[col] )
    .filter( v => !isNullish( v ) && typeof v !== 'boolean' && Number.isFinite( Number( v ) ) )
    .map( v => Number( v ) );
}

export function percentile( sorted: number[], p: number ): number {
  // Linear interpolation method (same as numpy/Excel PERCENTILE.INC)
  const idx = p * ( sorted.length - 1 );
  const lo = Math.floor( idx );
  const hi = Math.ceil( idx );
  if ( lo === hi ) return sorted[lo];
  return sorted[lo] + ( sorted[hi] - sorted[lo] ) * ( idx - lo );
}

export function computeSkewness( sorted: number[] ): number {
  const n = sorted.length;
  if ( n < 3 ) return 0;
  const mean = sorted.reduce( ( s, v ) => s + v, 0 ) / n;
  let m2 = 0;
  let m3 = 0;
  for ( const v of sorted ) {
    const d = v - mean;
    m2 += d * d;
    m3 += d * d * d;
  }
  m2 /= n;
  m3 /= n;
  const s = Math.sqrt( m2 );
  return s === 0 ? 0 : m3 / ( s * s * s );
}

export function computeOutliers( col: string, rows: Record<string, unknown>[] ) {
  const nums = getNumericValues( col, rows ).sort( ( a, b ) => a - b );
  if ( nums.length < 4 ) return null;
  const q1 = percentile( nums, 0.25 );
  const q3 = percentile( nums, 0.75 );
  const iqr = q3 - q1;
  const lower = q1 - 1.5 * iqr;
  const upper = q3 + 1.5 * iqr;
  const outlierCount = nums.filter( v => v < lower || v > upper ).length;
  const skewness = computeSkewness( nums );
  const outlierPct = nums.length > 0 ? ( outlierCount / nums.length ) * 100 : 0;
  return { column: col, outlierCount, outlierPct, lower, upper, q1, q3, iqr, median: percentile( nums, 0.5 ), skewness, n: nums.length };
}

export function computeProfile( columnName: string, rows: Record<string, unknown>[] ): ColumnProfile {
  const values = rows.map( r => r[columnName] );
  const nullCount = values.filter( v => isNullish( v ) ).length;
  const nullPercent = rows.length > 0 ? ( nullCount / rows.length ) * 100 : 0;
  const nonNull = values.filter( v => !isNullish( v ) );

  // Distinct count (non-null unique values) — use full data for accuracy
  const distinctSet = new Set( nonNull.map( v => String( v ) ) );
  const distinctCount = distinctSet.size;

  // Frequency map for top values (uses full data for accuracy)
  const freqMap = buildFrequencyMap( nonNull );
  const topValues = [ ...freqMap.entries() ]
    .sort( ( a, b ) => b[1] - a[1] )
    .slice( 0, 5 )
    .map( ( [ value, count ] ) => ( { value, count } ) );

  // Min / max
  let min: number | string | undefined;
  let max: number | string | undefined;
  const allNumeric = nonNull.length > 0 && nonNull.every( v => typeof v !== 'boolean' && Number.isFinite( Number( v ) ) );

  let histogram: { bucket: string; count: number }[] | undefined;
  let mean: number | undefined;
  let stddev: number | undefined;

  if ( allNumeric && nonNull.length > 0 ) {
    const nums = nonNull.map( v => Number( v ) );
    let numMin = Infinity;
    let numMax = -Infinity;
    let sum = 0;
    for ( const n of nums ) {
      if ( n < numMin ) numMin = n;
      if ( n > numMax ) numMax = n;
      sum += n;
    }
    min = numMin;
    max = numMax;
    mean = sum / nums.length;

    // Standard deviation
    let sqDiffSum = 0;
    for ( const n of nums ) {
      sqDiffSum += ( n - mean ) * ( n - mean );
    }
    stddev = Math.sqrt( sqDiffSum / nums.length );

    // Histogram with 10 buckets
    const bucketCount = 10;
    const range = numMax - numMin;
    if ( range > 0 ) {
      const bucketSize = range / bucketCount;
      const buckets = Array.from( { length: bucketCount }, () => 0 );
      for ( const n of nums ) {
        let idx = Math.floor( ( n - numMin ) / bucketSize );
        if ( idx >= bucketCount ) idx = bucketCount - 1;
        buckets[idx]++;
      }
      histogram = buckets.map( ( count, i ) => {
        const lo = numMin + i * bucketSize;
        const hi = lo + bucketSize;
        return { bucket: `${ formatNum( lo ) } - ${ formatNum( hi ) }`, count };
      } );
    }
  } else if ( nonNull.length > 0 ) {
    const sorted = nonNull.map( v => String( v ) ).sort();
    min = sorted[0];
    max = sorted[sorted.length - 1];
  }

  return { columnName, distinctCount, nullCount, nullPercent, min, max, topValues, histogram, mean, stddev };
}
